/**
 * StripeConnectCard
 * Displays Stripe Connect status with connect/manage/disconnect actions.
 */

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import type { ConnectStatus } from "../services/earnings.service"

type StripeConnectCardProps = {
  connectStatus?: ConnectStatus
  isLoading: boolean
  isConnecting: boolean
  isDisconnecting: boolean
  isManaging: boolean
  onConnect: () => void
  onDisconnect: () => void
  onManage: () => void
}

export function StripeConnectCard({
  connectStatus,
  isLoading,
  isConnecting,
  isDisconnecting,
  isManaging,
  onConnect,
  onDisconnect,
  onManage,
}: StripeConnectCardProps) {
  if (isLoading) {
    return <StripeConnectCardSkeleton />
  }

  const isConnected = connectStatus?.connected ?? false
  const isChargeReady = connectStatus?.destinationChargeReady !== false

  return (
    <div className="flex justify-between xl:items-start">
      <div className="min-w-0">
        <div className="flex items-center gap-2.5 mt-1.75">
          <span className="text-[15px] font-semibold text-primary leading-6.25">
            Stripe Account
          </span>
          {isConnected && (
            <span className="px-1.5 py-0.5 rounded-[5px] bg-brand-olive text-[#F2F0EBE6] text-[13px] leading-5">
              Connected
            </span>
          )}
        </div>
        <p className="text-[13px] text-body mt-2.75 max-w-[350px]">
          {isConnected
            ? "You're ready to receive payouts. Earnings will be sent to your connected account."
            : "Takes about 5 minutes. This is how money from your clients gets to your bank account."}
        </p>
        {isConnected && !isChargeReady && (
          <p className="text-[13px] text-amber-400 mt-2 max-w-[420px]">
            Paid offerings are paused until your Stripe account is fully ready for charges and
            payouts.
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 xl:justify-end">
        {isConnected ? (
          <div className="flex flex-wrap gap-2.5">
            <Button
              variant="outline"
              size="sm"
              className="h-[50px] px-10.25 py-3.75 uppercase border-[#F2F0EB] text-sm font-semibold text-primary"
              onClick={onManage}
              isLoading={isManaging}
            >
              Manage
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="h-[50px] pl-6.75 pr-6.5 py-3.75 uppercase bg-brand-terracotta text-primary text-sm font-semibold"
              onClick={onDisconnect}
              isLoading={isDisconnecting}
            >
              Disconnect
            </Button>
          </div>
        ) : (
          <Button
            variant="primary"
            size="sm"
            className="h-[50px] px-6 uppercase"
            onClick={onConnect}
            isLoading={isConnecting}
          >
            Connect with Stripe
          </Button>
        )}
      </div>
    </div>
  )
}

function StripeConnectCardSkeleton() {
  return (
    <div className="flex justify-between xl:items-start">
      <div className="min-w-0 space-y-2.75">
        <div className="flex items-center gap-2.5 mt-1.75">
          <Skeleton className="h-[22px] w-[120px]" />
          <Skeleton className="h-[20px] w-[80px] rounded-[5px]" />
        </div>
        <Skeleton className="h-[16px] w-[350px] max-w-full" />
      </div>
      <div className="flex items-center gap-2.5 xl:justify-end">
        <Skeleton className="h-[50px] w-[120px]" />
        <Skeleton className="h-[50px] w-[110px]" />
      </div>
    </div>
  )
}
